import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { JobContext } from '../../../contexts/ContextCreator';
import { ScrollVisWrapper } from '../../Elements/ScrollTopButton/ScrollTopButton';
import JobListing from '../../JobV3/Elements/JobListing/JobListing';

import './JobList.scss';

const getInsertKey = (num, id = 1) => `${num}-inserted-component-${id}`;

const JobList = ({
    hasOnlySinglePageOfResults,
    repeatingCadence,
    repeatingInserts,
    showExpired,
    staticInsert,
    zeroStateComponent,
}) => {
    // gather the algolia props along with the other data needed from the context
    const { content = [], algoliaQueryId, algoliaIndexName } = useContext(
        JobContext
    );

    // no content, return a no results component
    if (!content.length)
        return <div className="no-results">{zeroStateComponent}</div>;

    return (
        <div className="component-JobComponents-JobList">
            {(content || []).reduce((components, item, i) => {
                // build an algolia tracking object prop to pass to
                // each listing on the page. Note: algolia uses a 1-based
                // position, not 0 based.
                // We pass the prop to `JobListing` below.
                const algoliaTracking = {
                    positionInListing: i + 1,
                    queryID: algoliaQueryId,
                    indexName: algoliaIndexName,
                };

                const jobCard =
                    i === 20 ? (
                        <ScrollVisWrapper key={item.hash}>
                            <JobListing
                                {...item}
                                showExpired={showExpired}
                                algoliaTracking={algoliaTracking}
                            />
                        </ScrollVisWrapper>
                    ) : (
                        <JobListing
                            key={item.hash}
                            {...item}
                            showExpired={showExpired}
                            algoliaTracking={algoliaTracking}
                        />
                    );

                // never insert anything other than a job card as the first item
                // [FGB-5414] identified that a card SHOULD be at the second to last position in the
                // listing, for example, when there are 21, 31, 41 listings in a result set.
                if (i === 0) return [...components, jobCard];

                if (
                    staticInsert.component &&
                    i === staticInsert.insertedIndex
                ) {
                    const insertComponent = React.cloneElement(
                        staticInsert.component,
                        { key: 'static-job-component-key' }
                    );
                    return [...components, insertComponent, jobCard];
                }

                const numRepeatingInserts = repeatingInserts.length;

                if (numRepeatingInserts > 0 && i % repeatingCadence === 0) {
                    // derive which element in the inserts array we need based on our total iterations
                    const numIterations = i / repeatingCadence;
                    const currentElementIndex =
                        (numIterations - 1) % numRepeatingInserts;

                    const currentInsert = repeatingInserts[currentElementIndex];

                    return currentInsert != null // if its not null/undefined - clone and insert it
                        ? [
                              ...components,
                              React.cloneElement(currentInsert, {
                                  key: getInsertKey(i),
                              }),
                              jobCard,
                          ]
                        : [...components, jobCard];
                }

                return [...components, jobCard];
            }, [])}
            {/*
                if there are less results than a single pagination count, inject the featured ad at the
                end of the available listings (e.g., 1-20 hits). Less than or greater than that is handled
                by existing logic.

                if the total result count is even to a page count (20,40,60...), make sure to inject an ad at the end,
                per FGB-5414
            */}
            {((hasOnlySinglePageOfResults ||
                content.length % repeatingCadence === 0) &&
                repeatingInserts[1]) ||
                null}
        </div>
    );
};

export default JobList;

JobList.propTypes = {
    hasOnlySinglePageOfResults: PropTypes.bool,
    zeroStateComponent: PropTypes.node,
    showExpired: PropTypes.bool,
    staticInsert: PropTypes.exact({
        component: PropTypes.node,
        insertedIndex: PropTypes.number.isRequired,
    }),
    repeatingInserts: PropTypes.arrayOf(PropTypes.node), // order is important!
    repeatingCadence: PropTypes.number,
};

JobList.defaultProps = {
    hasOnlySinglePageOfResults: false,
    zeroStateComponent: null,
    showExpired: false,
    staticInsert: {
        component: null,
        insertedIndex: 0,
    },
    repeatingInserts: [],
    repeatingCadence: 10,
};
